import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useEntity } from '@backstage/plugin-catalog-react';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { ErrorPanel, MissingAnnotationEmptyState, Progress } from '@backstage/core-components';
import {
  Box,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

interface OrcaAttackPath {
  id: string;
  title: string;
  riskScore: number;
  impactScore: number;
  status: string;
  clusterName?: string;
}

interface OrcaAlert {
  id: string;
  title: string;
  category: string;
  riskLevel: string;
  source: string;
  orcaScore: number;
  description?: string;
  recommendation?: string;
  remediationSteps?: string[];
  clusterName?: string;
}

interface OrcaAlertCounts {
  critical: number;
  high: number;
  medium: number;
  low: number;
  informational: number;
}

interface OrcaSecurityData {
  kubernetesId: string;
  fetchedAt: string;
  alertCounts: OrcaAlertCounts;
  topAlerts: OrcaAlert[];
  attackPaths: OrcaAttackPath[];
}

const SEVERITY_COLORS: Record<string, string> = {
  critical:      '#d32f2f',
  high:          '#f57c00',
  medium:        '#f9a825',
  low:           '#1976d2',
  informational: '#757575',
};

const ORCA_ALERT_BASE_URL = 'https://eu.app.orcasecurity.io/alerts';
const ORCA_ATTACK_PATH_BASE_URL = 'https://eu.app.orcasecurity.io/attack-paths';

const SEVERITY_ROWS: { key: keyof OrcaAlertCounts; label: string }[] = [
  { key: 'critical',      label: 'CRITICAL' },
  { key: 'high',          label: 'HIGH' },
  { key: 'medium',        label: 'MEDIUM' },
  { key: 'low',           label: 'LOW' },
  { key: 'informational', label: 'INFORMATIONAL' },
];

type AttackPathRisk = 'high' | 'medium' | 'low';

const ATTACK_PATH_RISK_LEVELS: { key: AttackPathRisk; label: string; color: string; min: number }[] = [
  { key: 'high',   label: 'HIGH',   color: '#d32f2f', min: 7 },
  { key: 'medium', label: 'MEDIUM', color: '#f57c00', min: 4 },
  { key: 'low',    label: 'LOW',    color: '#1976d2', min: 0 },
];

const VALID_SEVERITIES = new Set(['critical', 'high', 'medium', 'low', 'informational']);
const VALID_ATTACK_RISKS = new Set<AttackPathRisk>(['high', 'medium', 'low']);

function getAttackPathRisk(score: number): AttackPathRisk {
  if (score >= 7) return 'high';
  if (score >= 4) return 'medium';
  return 'low';
}

function FilterButton({
  color, active, clickable, onClick, children,
}: {
  color: string;
  active: boolean;
  clickable: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={!clickable}
      aria-pressed={active}
      style={{
        opacity: clickable ? 1 : 0.5,
        background: color,
        color: 'white',
        borderRadius: 4,
        padding: '2px 10px',
        fontWeight: 600,
        fontSize: 13,
        cursor: clickable ? 'pointer' : 'default',
        outline: active ? '2px solid white' : 'none',
        outlineOffset: '-2px',
        border: 'none',
        fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  );
}

export function OrcaSecurityTab(): JSX.Element {
  const { entity } = useEntity();
  const location = useLocation();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const annotations = entity.metadata.annotations ?? {};
  const selectorMatch = annotations['backstage.io/kubernetes-label-selector']?.match(/github\.repository=([a-zA-Z0-9._\/-]+)/);
  const kubernetesId = (annotations['backstage.io/kubernetes-id'] ?? selectorMatch?.[1])?.toLowerCase();

  const params = new URLSearchParams(location.search);
  const rawSeverity = params.get('severity');
  const rawAttackRisk = params.get('attackRisk');
  const initialSeverity = rawSeverity && VALID_SEVERITIES.has(rawSeverity) ? rawSeverity : null;
  const initialAttackRisk = rawAttackRisk && VALID_ATTACK_RISKS.has(rawAttackRisk as AttackPathRisk)
    ? (rawAttackRisk as AttackPathRisk)
    : null;
  const [filterSeverity, setFilterSeverity] = useState<string | null>(initialSeverity);
  const [filterAttackRisk, setFilterAttackRisk] = useState<AttackPathRisk | null>(initialAttackRisk);
  const alertsRef = useRef<HTMLDivElement>(null);
  const attackPathsRef = useRef<HTMLDivElement>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [pageMap, setPageMap] = useState<Map<string, number>>(new Map());
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [attackPathsRowsPerPage, setAttackPathsRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<OrcaSecurityData | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!kubernetesId) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('orca');
        const response = await fetchApi.fetch(
          `${baseUrl}/security-summary?kubernetesId=${encodeURIComponent(kubernetesId)}`,
        );
        if (response.status === 404) {
          setNotFound(true);
          return;
        }
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error((body as any).error ?? `HTTP ${response.status}`);
        }
        setData(await response.json());
      } catch (e) {
        setError(e as Error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [kubernetesId, discoveryApi, fetchApi]);

  useEffect(() => {
    if (!data) return;
    const hash = location.hash;
    if (hash === '#attack-paths') {
      attackPathsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (hash === '#alerts') {
      alertsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [data, location.hash]);

  if (!kubernetesId) {
    return <MissingAnnotationEmptyState annotation="backstage.io/kubernetes-id" />;
  }
  if (loading) return <Progress />;
  if (notFound) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="h6" gutterBottom>Orca data is not available yet</Typography>
        <Typography variant="body2" color="textSecondary">
          Data will appear after the first polling cycle (~30 seconds after startup).
        </Typography>
      </Box>
    );
  }
  if (error) return <ErrorPanel error={error} />;
  if (!data) return <></>;

  const { alertCounts, topAlerts, fetchedAt, attackPaths } = data;

  const filteredAlerts = filterSeverity
    ? topAlerts.filter(a => a.riskLevel === filterSeverity)
    : topAlerts;

  const filteredAttackPaths = filterAttackRisk
    ? attackPaths.filter(ap => getAttackPathRisk(ap.riskScore) === filterAttackRisk)
    : attackPaths;

  const groupedAttackPaths = new Map<string, OrcaAttackPath[]>();
  for (const ap of filteredAttackPaths) {
    const key = ap.clusterName ?? 'Other';
    if (!groupedAttackPaths.has(key)) groupedAttackPaths.set(key, []);
    groupedAttackPaths.get(key)!.push(ap);
  }

  // Group alerts by cluster
  const grouped = new Map<string, OrcaAlert[]>();
  for (const alert of filteredAlerts) {
    const key = alert.clusterName ?? 'Other';
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(alert);
  }

  // Attack path counts by risk level
  const attackPathCounts: Record<AttackPathRisk, number> = { high: 0, medium: 0, low: 0 };
  for (const ap of attackPaths) {
    attackPathCounts[getAttackPathRisk(ap.riskScore)]++;
  }

  function toggleSeverityFilter(key: string, count: number) {
    if (count === 0) return;
    setFilterSeverity(prev => (prev === key ? null : key));
    setPageMap(new Map());
  }

  function toggleAttackRiskFilter(key: AttackPathRisk, count: number) {
    if (count === 0) return;
    setFilterAttackRisk(prev => (prev === key ? null : key));
    setPageMap(new Map());
  }

  function toggleExpand(id: string) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function getPage(cluster: string) { return pageMap.get(cluster) ?? 0; }
  function setPage(cluster: string, page: number) {
    setPageMap(prev => new Map(prev).set(cluster, page));
  }

  const formattedDate = new Date(fetchedAt).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  return (
    <Box p={2}>

      {/* ── Alerts ─────────────────────────────────────────────────────── */}
      <div ref={alertsRef}><Box mb={3}>
        <Typography variant="h6" gutterBottom>Alerts</Typography>
        <Box display="flex" flexWrap="wrap" style={{ gap: 8 }} mb={2}>
          {SEVERITY_ROWS.map(({ key, label }) => {
            const count = alertCounts[key];
            return (
              <FilterButton
                key={key}
                color={SEVERITY_COLORS[key]}
                active={filterSeverity === key}
                clickable={count > 0}
                onClick={() => toggleSeverityFilter(key, count)}
              >
                {count} {label}
              </FilterButton>
            );
          })}
        </Box>

        {filteredAlerts.length === 0 ? (
          <Typography variant="body2" color="textSecondary">No alerts found</Typography>
        ) : (
          [...grouped.entries()].map(([clusterName, clusterAlerts], groupIndex) => {
            const page = getPage(clusterName);
            return (
              <Box key={clusterName}>
                {grouped.size > 1 && (
                  <Box mt={groupIndex > 0 ? 2 : 0} mb={0.5}>
                    {groupIndex > 0 && <Divider />}
                    <Typography variant="caption" color="textSecondary" style={{ paddingLeft: 2, display: 'block', marginTop: 4 }}>
                      {clusterName}
                    </Typography>
                  </Box>
                )}
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>Severity</TableCell>
                      <TableCell>Score</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clusterAlerts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).flatMap(alert => {
                      const expanded = expandedIds.has(alert.id);
                      return [
                        <TableRow
                          key={alert.id}
                          hover
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => toggleExpand(alert.id)}
                        >
                          <TableCell padding="none" style={{ width: 32 }}>
                            <IconButton size="small">
                              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <span style={{
                              background: SEVERITY_COLORS[alert.riskLevel] ?? '#757575',
                              color: 'white',
                              borderRadius: 4,
                              padding: '2px 8px',
                              fontSize: 11,
                              fontWeight: 600,
                              textTransform: 'uppercase',
                            }}>
                              {alert.riskLevel}
                            </span>
                          </TableCell>
                          <TableCell>{alert.orcaScore.toFixed(1)}</TableCell>
                          <TableCell>{alert.title}</TableCell>
                          <TableCell>{alert.category}</TableCell>
                          <TableCell>{alert.source}</TableCell>
                          <TableCell padding="none" style={{ width: 40 }}>
                            <IconButton
                              size="small"
                              component="a"
                              href={`${ORCA_ALERT_BASE_URL}/${alert.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>,
                        expanded ? (
                          <TableRow key={`${alert.id}-details`}>
                            <TableCell colSpan={7} style={{ paddingBottom: 12, paddingTop: 4 }}>
                              <Box px={1}>
                                {alert.description && (
                                  <Box mb={1}>
                                    <Typography variant="caption" style={{ fontWeight: 600, display: 'block' }}>Description</Typography>
                                    <Typography variant="body2">{alert.description}</Typography>
                                  </Box>
                                )}
                                {alert.recommendation && (
                                  <Box mb={1}>
                                    <Typography variant="caption" style={{ fontWeight: 600, display: 'block' }}>Recommendation</Typography>
                                    <Typography variant="body2">{alert.recommendation}</Typography>
                                  </Box>
                                )}
                                {alert.remediationSteps && alert.remediationSteps.length > 0 && (
                                  <Box>
                                    <Typography variant="caption" style={{ fontWeight: 600, display: 'block' }}>Remediation Steps</Typography>
                                    {alert.remediationSteps.map((step, i) => (
                                      <Typography key={i} variant="body2">{step}</Typography>
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ) : null,
                      ].filter(Boolean);
                    })}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[20, 50, 100]}
                  component="div"
                  count={clusterAlerts.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_e, newPage) => setPage(clusterName, newPage)}
                  onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPageMap(new Map()); }}
                />
              </Box>
            );
          })
        )}
      </Box></div>

      {/* ── Attack Paths ───────────────────────────────────────────────── */}
      <div ref={attackPathsRef}><Box mb={2}>
        <Typography variant="h6" gutterBottom>Attack Paths</Typography>
        <Box display="flex" flexWrap="wrap" style={{ gap: 8 }} mb={2}>
          {ATTACK_PATH_RISK_LEVELS.map(({ key, label, color }) => {
            const count = attackPathCounts[key];
            return (
              <FilterButton
                key={key}
                color={color}
                active={filterAttackRisk === key}
                clickable={count > 0}
                onClick={() => toggleAttackRiskFilter(key, count)}
              >
                {count} {label}
              </FilterButton>
            );
          })}
        </Box>

        {filteredAttackPaths.length === 0 ? (
          <Typography variant="body2" color="textSecondary">No attack paths found for this service</Typography>
        ) : (
          [...groupedAttackPaths.entries()].map(([clusterName, clusterPaths], groupIndex) => {
            const page = pageMap.get(`ap-${clusterName}`) ?? 0;
            return (
              <Box key={clusterName}>
                {groupedAttackPaths.size > 1 && (
                  <Box mt={groupIndex > 0 ? 2 : 0} mb={0.5}>
                    {groupIndex > 0 && <Divider />}
                    <Typography variant="caption" color="textSecondary" style={{ paddingLeft: 2, display: 'block', marginTop: 4 }}>
                      {clusterName}
                    </Typography>
                  </Box>
                )}
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>Risk Score</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Impact Score</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clusterPaths
                      .slice(page * attackPathsRowsPerPage, (page + 1) * attackPathsRowsPerPage)
                      .flatMap(ap => {
                        const expanded = expandedIds.has(ap.id);
                        return [
                          <TableRow
                            key={ap.id || ap.title}
                            hover
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => toggleExpand(ap.id)}
                          >
                            <TableCell padding="none" style={{ width: 32 }}>
                              <IconButton size="small">
                                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              <span style={{
                                background: ATTACK_PATH_RISK_LEVELS.find(l => l.key === getAttackPathRisk(ap.riskScore))?.color ?? '#1976d2',
                                color: 'white',
                                borderRadius: 4,
                                padding: '2px 8px',
                                fontWeight: 600,
                                fontSize: 12,
                              }}>
                                {ap.riskScore.toFixed(1)}
                              </span>
                            </TableCell>
                            <TableCell>{ap.title}</TableCell>
                            <TableCell>{ap.impactScore.toFixed(1)}</TableCell>
                            <TableCell padding="none" style={{ width: 40 }}>
                              <IconButton
                                size="small"
                                component="a"
                                href={`${ORCA_ATTACK_PATH_BASE_URL}/${ap.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={e => e.stopPropagation()}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>,
                          expanded ? (
                            <TableRow key={`${ap.id}-details`}>
                              <TableCell colSpan={5} style={{ paddingBottom: 12, paddingTop: 4 }}>
                                <Box px={1}>
                                  <Box mb={0.5}>
                                    <Typography variant="caption" style={{ fontWeight: 600, display: 'block' }}>Status</Typography>
                                    <Typography variant="body2" style={{ textTransform: 'capitalize' }}>{ap.status}</Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" style={{ fontWeight: 600, display: 'block' }}>Impact Score</Typography>
                                    <Typography variant="body2">{ap.impactScore.toFixed(1)}</Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ) : null,
                        ].filter(Boolean);
                      })}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={clusterPaths.length}
                  rowsPerPage={attackPathsRowsPerPage}
                  page={page}
                  onPageChange={(_e, newPage) => setPage(`ap-${clusterName}`, newPage)}
                  onRowsPerPageChange={e => { setAttackPathsRowsPerPage(parseInt(e.target.value, 10)); }}
                />
              </Box>
            );
          })
        )}
      </Box></div>

      <Typography variant="caption" color="textSecondary">
        Last updated: {formattedDate}
      </Typography>
    </Box>
  );
}
